import type {CSSProperties} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

const TextAreaInput = styled(
  'textarea',
  (
    {
      $invalid,
      disabled,
      rows,
    }: {
      $invalid?: boolean;
      disabled?: boolean;
      rows?: number;
    },
    designColors,
  ) => ({
    appearance: 'none',
    backgroundColor: designColors.contentBackground,
    boxShadow: $invalid
      ? '0 0 0 1px #EF6B6B, 0 0 0 0 #c9d9e0'
      : '0 0 0 1px #c6caca, 0 0 0 0 #c9d9e0',
    border: 0,
    borderRadius: 4,
    color: designColors.contentText,
    display: 'flex',
    flex: '0 0 100%',
    fontSize: 14,
    lineHeight: '20px',
    minHeight: rows ? undefined : 150,
    opacity: disabled ? 0.5 : 1,
    padding: '8px',
    resize: 'vertical',
    transition: 'box-shadow 0.2s, opacity 0.2s',
    width: '100%',

    ':focus': {
      boxShadow: $invalid
        ? '0 0 0 1px #EF6B6B, 0 0 0 4px #FEDEDE'
        : '0 0 0 1px #9eb4c0, 0 0 0 4px #c9d9e0',
      outline: 'none',
    },
  }),
  {className: 'TextArea'},
);

type Props = {
  disabled?: boolean;
  invalid?: boolean;
  maxLength?: number;
  minLength?: number;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLTextAreaElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLTextAreaElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLTextAreaElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLTextAreaElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLTextAreaElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLTextAreaElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLTextAreaElement>['onKeyPress'];
  }>;
  onChange?: (value: string) => void;
  onRef?: React.Ref<HTMLTextAreaElement | null>;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  resize?: 'both' | 'horizontal' | 'vertical' | 'none';
  rows?: number;
  style?: CSSProperties;
  tabIndex?: number;
  value: string;
};

const TextArea = (
  {invalid, nativeEvents, onChange, value, ...rest}: Props,
  ref: React.Ref<HTMLTextAreaElement>,
) => {
  const nativeOnChange = nativeEvents?.onChange;
  const handleChange = React.useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      e.preventDefault();

      onChange?.((e.target as HTMLTextAreaElement).value);
      nativeOnChange?.(e);
    },
    [onChange, nativeOnChange],
  );
  return (
    <TextAreaInput
      $invalid={invalid}
      {...rest}
      dir="auto"
      {...nativeEvents}
      onChange={handleChange}
      ref={ref}
      value={value}
    />
  );
};

export default React.forwardRef<HTMLTextAreaElement, Props>(TextArea);
